import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  Badge, Button, Card, EmptyState, Note, Skel, Sym, Textarea, usePageTitle,
} from '../../components/app2';
import { strapiService } from '../../utils/api';
import billingService from '../../utils/billingService';
import AlertService from '../../utils/alertService';

/**
 * Ficha de un prospecto (rediseño jul-2026).
 *
 * Misma información y las mismas dos acciones (guardar notas internas y marcar
 * perdido); cambia el molde: tarjetas del sistema, etiquetas de estado y
 * temperatura del rediseño, y el nombre del prospecto como título del header en
 * lugar de repetirlo dentro de la página.
 */

const STATUS_LABEL = {
  new: 'Nuevo', contacted: 'Contactado', qualified: 'Calificado',
  escalated: 'Escalado', converted: 'Convertido', lost: 'Perdido',
};
const STATUS_TONE = {
  new: 'brand', contacted: 'brand', qualified: 'warn',
  escalated: 'warn', converted: 'ok', lost: 'neutral',
};
const STATUS_ICON = {
  new: 'fiber_new', contacted: 'forum', qualified: 'verified',
  escalated: 'priority_high', converted: 'task_alt', lost: 'do_not_disturb_on',
};

const TEMP_LABEL = { unevaluated: 'Sin evaluar', cold: 'Frío', warm: 'Tibio', hot: 'Caliente' };
const TEMP_TONE = { unevaluated: 'neutral', cold: 'brand', warm: 'warn', hot: 'danger' };
const TEMP_ICON = {
  unevaluated: 'help', cold: 'ac_unit', warm: 'device_thermostat', hot: 'local_fire_department',
};

/* Los cuatro valores del enum `source` del content-type, en castellano. */
const SOURCE_LABEL = {
  whatsapp_direct: 'WhatsApp',
  landing: 'Sitio web',
  referral: 'Recomendación',
  other: 'Otro',
};

/* Las llaves de "datos captados" las escribe el asistente (giro, ciudad,
   razon_social…). No se traducen —son libres— pero sí se presentan legibles. */
const legible = (llave) => {
  const texto = String(llave).replace(/[_-]+/g, ' ').trim();
  return texto.charAt(0).toUpperCase() + texto.slice(1);
};

const fmtDate = (d) => (d ? new Date(d).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

/** Dato suelto: etiqueta chica arriba, valor abajo. */
const Dato = ({ label, children }) => (
  <div style={{ minWidth: 0 }}>
    <div className="a2-tcell-muted" style={{ marginBottom: 3 }}>{label}</div>
    <div className="a2-tcell-strong" style={{ wordBreak: 'break-word' }}>{children ?? '—'}</div>
  </div>
);

const AdminProspectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [prospect, setProspect] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState('');
  const [planNames, setPlanNames] = useState({});

  usePageTitle({ kicker: 'Plataforma', title: prospect?.name || null });

  // Catálogo de planes: traduce "tier-50" al nombre que ve el cliente. Si el
  // catálogo no carga, se muestra el identificador tal cual.
  useEffect(() => {
    (async () => {
      try {
        const res = await billingService.listPlans();
        const porSlug = {};
        (Array.isArray(res?.data) ? res.data : []).forEach((p) => {
          if (p?.slug) porSlug[p.slug] = p.name;
        });
        setPlanNames(porSlug);
      } catch (e) {
        console.error('Error cargando catálogo de planes:', e);
      }
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const p = await strapiService.prospects.get(id);
      setProspect(p);
      setNotes(p?.notes || '');
    } catch (e) {
      console.error('Error cargando prospecto:', e);
      AlertService?.error('Error', 'No se pudo cargar el prospecto.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const patch = async (body, successMsg) => {
    setSaving(true);
    try {
      const updated = await strapiService.prospects.update(id, body);
      setProspect(updated);
      if (successMsg) AlertService?.success('Listo', successMsg);
    } catch (e) {
      AlertService?.error('Error', e?.response?.data?.error?.message || 'No se pudo actualizar.');
    } finally {
      setSaving(false);
    }
  };

  const markLost = async () => {
    const ok = await AlertService?.confirm('Marcar como perdido', '¿Marcar este prospecto como perdido?');
    if (ok) patch({ status: 'lost' }, 'Prospecto marcado como perdido.');
  };

  const volver = (
    <Button
      variant="ghost"
      size="sm"
      icon="arrow_back"
      onClick={() => navigate('/app/admin/prospects')}
      style={{ marginBottom: 14 }}
    >
      Volver a prospectos
    </Button>
  );

  if (loading) {
    return (
      <div className="a2-anim-screen">
        {volver}
        <div className="a2-grid-2-wide">
          <Card>
            <Skel w={180} h={18} style={{ marginBottom: 20 }} />
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} style={{ marginBottom: 16 }}>
                <Skel w={90} h={10} style={{ marginBottom: 7 }} />
                <Skel w="72%" h={14} />
              </div>
            ))}
          </Card>
          <Card>
            <Skel w={120} h={16} style={{ marginBottom: 20 }} />
            {[1, 2, 3, 4].map((i) => (
              <div key={i} style={{ marginBottom: 16 }}>
                <Skel w={80} h={10} style={{ marginBottom: 7 }} />
                <Skel w="62%" h={14} />
              </div>
            ))}
          </Card>
        </div>
      </div>
    );
  }

  if (!prospect) {
    return (
      <div className="a2-anim-screen">
        {volver}
        <EmptyState
          icon="person_off"
          size="lg"
          title="Prospecto no encontrado"
          text="Puede que se haya eliminado o que el enlace esté mal."
          action={<Button icon="arrow_back" onClick={() => navigate('/app/admin/prospects')}>Volver a prospectos</Button>}
        />
      </div>
    );
  }

  const ficha = prospect.ficha || null;
  const data = prospect.data || {};
  const puedeMarcarPerdido = prospect.status !== 'converted' && prospect.status !== 'lost';

  return (
    <div className="a2-anim-screen">
      <Helmet>
        <title>{prospect.name ? `${prospect.name} - Prospectos` : 'Prospecto'} - PaySlip</title>
      </Helmet>

      {volver}

      {/* Contacto + estado + acción */}
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 12, marginBottom: 16,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <Badge tone={STATUS_TONE[prospect.status] || 'neutral'} icon={STATUS_ICON[prospect.status] || 'label'}>
            {STATUS_LABEL[prospect.status] || prospect.status}
          </Badge>
          <Badge
            tone={TEMP_TONE[prospect.temperature] || 'neutral'}
            icon={TEMP_ICON[prospect.temperature] || 'help'}
          >
            {TEMP_LABEL[prospect.temperature] || prospect.temperature}
          </Badge>
          <span className="a2-tcell-muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Sym name="call" size={16} style={{ color: 'var(--faint)' }} />
            <span className="a2-mono">{prospect.phone || '—'}</span>
            {prospect.email ? <> · {prospect.email}</> : null}
          </span>
        </div>

        {puedeMarcarPerdido ? (
          <Button
            variant="outline"
            size="sm"
            icon="do_not_disturb_on"
            disabled={saving}
            onClick={markLost}
            style={{ marginLeft: 'auto' }}
          >
            Marcar perdido
          </Button>
        ) : null}
      </div>

      <div className="a2-grid-2-wide">
        {/* Columna principal: ficha + datos + notas */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          <Card>
            <h3 className="a2-card-title" style={{ fontSize: '1.1rem' }}>Ficha</h3>
            <p className="a2-card-sub" style={{ marginBottom: 18 }}>
              Lo que el asistente entendió de la conversación
            </p>

            {ficha ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Dato label="Quién es">{ficha.quienEs}</Dato>
                <Dato label="Qué quiere">{ficha.queQuiere}</Dato>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 16 }}>
                  <Dato label="Plan sugerido">{ficha.planSugerido}</Dato>
                  <Dato label="Score">{ficha.score != null ? `${ficha.score}/100` : '—'}</Dato>
                </div>

                {Array.isArray(ficha.senales) && ficha.senales.length > 0 ? (
                  <div>
                    <div className="a2-tcell-muted" style={{ marginBottom: 6 }}>Señales</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {ficha.senales.map((s, i) => (
                        <Badge key={i} icon="bolt">{s}</Badge>
                      ))}
                    </div>
                  </div>
                ) : null}

                <Dato label="Acción recomendada">{ficha.accionRecomendada}</Dato>

                <p className="a2-tcell-muted" style={{ margin: 0 }}>
                  Ficha generada {fmtDate(prospect.ficha_generated_at)}
                </p>
              </div>
            ) : (
              <EmptyState
                icon="pending_actions"
                title="Aún no hay ficha"
                text="El asistente la genera al cualificar al prospecto en la conversación."
                className="a2-card"
                style={{ border: 'none', padding: '28px 12px' }}
              />
            )}
          </Card>

          {Object.keys(data).length > 0 ? (
            <Card>
              <h3 className="a2-card-title" style={{ fontSize: '1.1rem' }}>Datos captados</h3>
              <p className="a2-card-sub" style={{ marginBottom: 18 }}>
                Campos que el prospecto dio durante la conversación
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 16 }}>
                {Object.entries(data).map(([k, v]) => (
                  <Dato key={k} label={legible(k)}>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</Dato>
                ))}
              </div>
            </Card>
          ) : null}

          <Card>
            <h3 className="a2-card-title" style={{ fontSize: '1.1rem' }}>Notas internas</h3>
            <p className="a2-card-sub" style={{ marginBottom: 14 }}>
              Solo las ve el equipo; el prospecto nunca las recibe
            </p>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notas del equipo sobre este prospecto…"
              aria-label="Notas internas del prospecto"
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <Button
                size="sm"
                icon="save"
                disabled={saving || notes === (prospect.notes || '')}
                loading={saving && notes !== (prospect.notes || '')}
                onClick={() => patch({ notes }, 'Notas guardadas.')}
              >
                Guardar notas
              </Button>
            </div>
          </Card>
        </div>

        {/* Columna lateral: metadatos + conversión */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          <Card>
            <h3 className="a2-card-title" style={{ fontSize: '1.1rem' }}>Detalles</h3>
            <p className="a2-card-sub" style={{ marginBottom: 18 }}>Origen y seguimiento</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <Dato label="Fuente">{SOURCE_LABEL[prospect.source] || prospect.source}</Dato>
              <Dato label="Plan de interés">
                {prospect.interested_tier
                  ? (planNames[prospect.interested_tier] || prospect.interested_tier)
                  : '—'}
              </Dato>
              <Dato label="Creado">{fmtDate(prospect.createdAt)}</Dato>
              <Dato label="Último contacto">{fmtDate(prospect.last_contact_at)}</Dato>
            </div>
          </Card>

          {prospect.status === 'converted' && prospect.converted_company ? (
            <Note tone="ok" icon="task_alt">
              <strong>Convertido.</strong> Empresa: {prospect.converted_company?.name}
              {prospect.converted_user ? <> · Administrador: {prospect.converted_user?.email}</> : null}
            </Note>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AdminProspectDetail;
